<template>
  <div>
    <recipients-biometry
      v-if="shouldShowRecipientsBiometry"
      :id="createdRecipient.id"
      :name="createdRecipient.name"
    />
    <div
      v-else
      class="el-section"
    >
      <div class="emd-container _highlight_header _header_spaced _footer_end">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">
            {{ $t('recipients.create.title') }}
          </h2>
        </div>

        <div
          v-if="showAlert"
          class="emd-container__content"
        >
          <emd-alert
            :variant="formError ? 'danger' : 'success'"
            @cancel="showAlert = false"
            :title="
              formError ? $t('alert.error-title') : $t('alert.success-title')
            "
          >
            <template slot="content">
              <div v-if="formError">
                <p
                  v-for="(error, index) in errors"
                  :key="index"
                >
                  {{ error }}
                </p>
              </div>
              <p v-else>{{ $t('recipients.create.success-msg') }}</p>
            </template>
          </emd-alert>
        </div>

        <form
          novalidate="true"
          action="javascript:void(0)"
          @submit="handleSubmit"
        >
          <div class="emd-container__content">
            <Identification
              :recipient="computedRecipient"
              :default_bank_account="default_bank_account"
              :vuelidate="$v"
            />

            <hr class="emd-divider eu-mb_5" />

            <Recipient
              :recipient="computedRecipient"
              :isCpf="isCpf"
              :vuelidate="$v"
            />
            <hr class="emd-divider eu-mb_5" />

            <BankAccount
              :default_bank_account="default_bank_account"
              :vuelidate="$v"
            />

            <hr class="emd-divider eu-mb_5" />

            <PrepaymentSettings
              :automatic_anticipation_settings="automatic_anticipation_settings"
              :vuelidate="$v"
            />

            <hr class="emd-divider eu-mb_5" />

            <TransferSettings
              :transfer_settings="transfer_settings"
              :transferSettingsIsInvalid.sync="transferSettingsIsInvalid"
            />
            <managing-partners
              v-if="showManagingPartners"
              @add="addManagingPartner"
              @remove="removeManagingPartner"
              :isCpf="isCpf"
              :managing_partners="computedRecipient.managing_partners"
            />
            <SelfDeclaredLegalRepresentativeInput
              v-if="
                showManagingPartners &&
                computedRecipient?.managing_partners?.length
              "
              :recipient.sync="computedRecipient"
            />
            <div class="emd-container__footer">
              <router-link
                tag="button"
                class="emd-btn _color_primary _type_text"
                type="button"
                :to="{ name: 'recipients' }"
                >{{ $t('recipients.create.button-cancel') }}</router-link
              >
              <EmdButton
                color="primary"
                :disabled="submitIsDisabled"
                type="submit"
              >
                <emd-loader v-if="loadingCreation"></emd-loader>
                <span v-else>{{ $t('recipients.create.button-ok') }}</span>
              </EmdButton>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { EmdLoader, EmdAlert, EmdButton } from 'emerald-vue/src/components'
import { isPartiallyDenied } from '@/enums/kyc.enum'

import SelfDeclaredLegalRepresentativeInput from '../Form/FormFields/SelfDeclaredLegalRepresentativeInput.vue'
import { formatErrorMessage } from '@/support/formatErrorMessage'
import ManagingPartners from '../Form/ManagingPartners.vue'
import Identification from '../Form/Identification.vue'
import Recipient from '../Form/Recipient.vue'
import BankAccount from '../Form/BankAccount.vue'
import PrepaymentSettings from '../Form/PrepaymentSettings.vue'
import TransferSettings from '../Form/TransferSettings.vue'

import {
  register_information as registerInformation,
  managing_partner as managingPartner
} from '../Form/fields'
import {
  personalUser,
  corporationUser,
  prepaymentValidations,
  defaultBankAccountValidations,
  transferSettingsValidations
} from '../Form/validations'

export default {
  components: {
    Identification,
    Recipient,
    BankAccount,
    PrepaymentSettings,
    TransferSettings,
    EmdLoader,
    EmdAlert,
    EmdButton,
    ManagingPartners,
    SelfDeclaredLegalRepresentativeInput,
    RecipientsBiometry: () =>
      import('../RecipientsBiometry/RecipientsBiometry.vue')
  },
  data () {
    return {
      loadingCreation: false,
      showAlert: false,
      formError: false,
      errors: [],
      recipient: {
        type: 'individual',
        document: '',
        name: '',
        email: '',
        description: ''
      },
      register_information: {},
      managing_partner: managingPartner,
      default_bank_account: {
        bank: '',
        branch_number: '',
        branch_check_digit: '',
        account_number: '',
        account_check_digit: '',
        type: 'checking',
        holder_type: 'individual',
        holder_document: '',
        holder_name: ''
      },
      transfer_settings: {
        transfer_enabled: false,
        transfer_interval: '',
        transfer_day: 0
      },
      transferSettingsIsInvalid: false,
      automatic_anticipation_settings: {
        enabled: false,
        volume_percentage: 100,
        delay: 365,
        type: ''
      },
      createdRecipient: {}
    }
  },
  validations () {
    const transferSettings = {
      transfer_day:
        this.transfer_settings.transfer_interval !== 'Daily'
          ? transferSettingsValidations.transfer_day
          : {},
      transfer_interval: transferSettingsValidations.transfer_interval
    }

    return {
      recipient: this.isCpf ? personalUser : corporationUser,
      default_bank_account: defaultBankAccountValidations,
      automatic_anticipation_settings: prepaymentValidations,
      transfer_settings: transferSettings
    }
  },
  computed: {
    ...mapGetters(['canCreateRecipient']),
    isCpf () {
      return this.computedRecipient.type === 'individual'
    },
    creationMethod () {
      return this.canCreateRecipient
        ? this.createRecipient
        : this.createRecipientV2
    },
    account () {
      return this.$store.getters.account
    },
    merchant () {
      return this.$store.getters.merchant
    },
    submitIsDisabled () {
      const antecipation = this.automatic_anticipation_settings.enabled
      const transfer = this.transfer_settings.transfer_enabled

      const recipientInvalid =
        /* this.$v.recipient.$invalid || */ this.$v.default_bank_account
          .$invalid
      const antecipationInvalid =
        this.$v.automatic_anticipation_settings.$invalid
      const transferInvalid = this.transferSettingsIsInvalid

      if (antecipation && !transfer) {
        return recipientInvalid || antecipationInvalid
      }
      if (!antecipation && transfer) {
        return recipientInvalid || transferInvalid
      }
      if (antecipation && transfer) {
        return recipientInvalid || antecipationInvalid || transferInvalid
      }

      return recipientInvalid
    },
    shouldShowRecipientsBiometry () {
      return Object.values(this.createdRecipient).length > 0
    },
    computedRecipient () {
      if (!this.canCreateRecipient) return this.register_information
      return this.recipient
    },
    showManagingPartners () {
      return !this.isCpf && !this.canCreateRecipient
    }
  },
  methods: {
    ...mapActions({
      createRecipientV2: 'CREATE_RECIPIENT_V2',
      createRecipient: 'CREATE_RECIPIENT'
    }),
    redirectToDetails (id) {
      const { merchant_id: merchantId, account_id: accountId } =
        this.$route.params
      this.$router.push({
        path: `/${merchantId}/${accountId}/${id}/recipient-details`
      })
    },
    formatErrors (error) {
      if (error.status === 412) {
        return [this.$t('recipients.create.error-form')]
      }

      return formatErrorMessage(
        error,
        this.$t('recipients.create.error-internal')
      )
    },
    resetState () {
      this.loadingCreation = false
      this.showAlert = false
      this.formError = false
      this.errors = []
      this.recipient = {
        type: 'individual',
        document: '',
        name: '',
        email: '',
        description: ''
      }
      this.initializeRegisterInformation()
      this.default_bank_account = {
        bank: '',
        branch_number: '',
        branch_check_digit: '',
        account_number: '',
        account_check_digit: '',
        type: 'checking',
        holder_type: 'individual',
        holder_document: '',
        holder_name: ''
      }
      this.transfer_settings = {
        transfer_enabled: false,
        transfer_interval: '',
        transfer_day: 0
      }
      this.automatic_anticipation_settings = {
        enabled: false,
        volume_percentage: 100,
        delay: 365,
        type: ''
      }
      this.createdRecipient = {}
    },
    initializeRegisterInformation () {
      this.register_information = {
        ...registerInformation,
        address: {
          ...registerInformation.address,
          reference_point: ''
        },
        phone_numbers: [
          {
            ddd: '',
            number: '',
            type: ''
          }
        ]
      }
    },
    addManagingPartner () {
      const managingPartnerCopy = { ...this.managing_partner }
      this.computedRecipient.managing_partners.push(managingPartnerCopy)
      this.managing_partner = {
        name: '',
        email: '',
        document: '',
        type: 'individual',
        mother_name: '',
        birth_date: '',
        monthly_income: 0,
        professional_occupation: '',
        self_declared_legal_representative: false,
        address: {
          street: '',
          complement: '',
          number: '',
          neighborhood: '',
          city: '',
          state: '',
          zip_code: '',
          reference_point: ''
        },
        phone_numbers: [
          {
            ddd: '',
            number: '',
            type: ''
          }
        ]
      }
    },
    removeManagingPartner (index) {
      this.computedRecipient.managing_partners.splice(index, 1)
    },
    handleSubmit () {
      this.loadingCreation = true

      const defaultBank = { ...this.default_bank_account }

      if (defaultBank.branch_check_digit === '') {
        delete defaultBank.branch_check_digit
      }
      if (!this.isCpf && !this.canCreateRecipient) {
        this.computedRecipient.main_address = this.computedRecipient.address
      }

      let body = {}
      if (!this.canCreateRecipient) {
        body.register_information = this.computedRecipient
        body.register_information.site_url =
          body.register_information?.site_url || null
      } else {
        body = { ...this.computedRecipient }
      }

      body.default_bank_account = defaultBank
      if (this.automatic_anticipation_settings.enabled) {
        body.automatic_anticipation_settings = {
          ...this.automatic_anticipation_settings
        }
      }

      if (this.transfer_settings.transfer_enabled) {
        body.transfer_settings = { ...this.transfer_settings }
        if (body.transfer_settings.transfer_interval === 'Daily')
          body.transfer_settings.transfer_day = 0
      }

      const params = {
        query: {
          merchant_id: this.$route.params.merchant_id,
          account_id: this.$route.params.account_id
        },
        body
      }

      this.creationMethod(params)
        .then(res => {
          this.loadingCreation = false
          this.showAlert = true
          this.formError = false

          const kycStatus = res.kyc_details?.status
          const shouldDisplayValidation =
            kycStatus && isPartiallyDenied(kycStatus)

          window.scrollTo(0, 0)
          setTimeout(() => {
            if (shouldDisplayValidation) {
              this.createdRecipient = res
            } else {
              this.redirectToDetails(res.id)
            }
            this.resetState()
          }, 2000)
        })
        .catch(err => {
          this.showAlert = true
          this.formError = true
          this.loadingCreation = false
          this.errors = this.formatErrors(err)
          window.scrollTo(0, 0)
        })
    }
  },
  mounted () {
    this.initializeRegisterInformation()
  }
}
</script>
